<template>
    <rkm-area
        v-if="model"
        no-margin-top
        title="Certificado digital"
        type="fieldset"
    >
        <rkm-form
            v-if="items.length === 0 || editing"
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-file
                        :disabled="editing"
                        :placeholder="editing ? 'CERTIFICADO-DIGITAL.PFX' : null"
                        :validations="validations.certificadoDigital"
                        accept=".pfx"
                        label="Certificado digital (A1 .PFX)"
                        v-model="model.certificadoDigital"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        :disabled="!model.certificadoDigital || editing"
                        :validations="validations.senha"
                        label="Senha"
                        type="password"
                        v-model="model.senha"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        :disabled="editing ? false : !model.certificadoDigital"
                        :validations="validations.identificadorSolicitante"
                        label="Identificador do solicitante"
                        number
                        v-model="model.identificadorSolicitante"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        :disabled="editing ? false : !model.certificadoDigital"
                        :validations="validations.cnsSolicitante"
                        label="CNS do solicitante"
                        v-mask="mask.cns"
                        v-model="model.cnsSolicitante"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-table
            v-else
            :fields="fields"
            :items="items"
            hide-filters
            hide-pagination
        >
            <template v-slot:cell(cnsSolicitante)="{item}">
                {{formatCns(item.cnsSolicitante)}}
            </template>

            <template v-slot:cell(actions)>
                <rkm-button
                    @click="download"
                    color="info"
                    small
                    title="Download das evidências"
                >
                    <rkm-icon icon="download" />
                </rkm-button>

                <rkm-button
                    @click="edit"
                    authorize="update-integracao-ria-parametrizacao"
                    color="primary"
                    small
                    title="Editar"
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    @click="destroy"
                    authorize="destroy-integracao-ria-parametrizacao"
                    color="danger"
                    small
                    title="Excluir"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>
    </rkm-area>
</template>

<script>
// Mixins.
import {error, formats, mask, repository, stringCase, unmask} from '@/mixins'

// Others.
import { mapActions } from 'vuex'

export default {
    computed: {
        validations() {
            return {
                certificadoDigital: {required: true},
                senha: {required: true},
                identificadorSolicitante: {
                    required: true,
                    min: 1,
                    max: 100,
                },
                cnsSolicitante: {
                    required: true,
                    cns: true,
                    min_max_without_mask: 15,
                },
            }
        }
    },
    created() {
        this.repositories = {
            parametrizacao: this.getConectaSusRepository('integracao.ria.parametrizacao'),
        }
    },
    data: () => ({
        editing: false,
        fields: [
            {key: 'certificadoDigital', label: 'Certificado digital'},
            {key: 'identificadorSolicitante', label: 'Identificador do solicitante'},
            {key: 'cnsSolicitante', label: 'CNS do solicitante'},
            {class: 'col-min text-center', key: 'actions', label: 'Ações'},
        ],
        items: [],
        model: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.parametrizacao.index()
                .then(response => {
                    this.items = Object.assign(response.data)
                    this.$emit('loaded', this.items.length === 0)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        create() {
            this.setLoading()

            this.repositories.parametrizacao.create()
                .then(response => this.model = Object.assign(response.data.model))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        store() {
            const request = new FormData()

            request.append('certificado_digital', this.model.certificadoDigital)
            request.append('senha', this.model.senha)
            request.append('identificador_solicitante', this.model.identificadorSolicitante)
            request.append('cns_solicitante', this.unMaskCns(this.model.cnsSolicitante))

            this.setLoading()

            this.repositories.parametrizacao.store(request)
                .then(() => {
                    this.showSaveNotification()
                    this.$emit('saved')
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        edit() {
            this.setLoading()

            this.repositories.parametrizacao.edit()
                .then(response => {
                    this.model = Object.assign(response.data.model)
                    this.model.cnsSolicitante = this.formatCns(this.model.cnsSolicitante)
                    this.editing = true
                    this.$emit('loaded', this.editing)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        update() {
            const request = {
                identificadorSolicitante: this.model.identificadorSolicitante,
                cnsSolicitante: this.unMaskCns(this.model.cnsSolicitante),
            }

            this.setLoading()

            this.repositories.parametrizacao.update(this.camelObjectKeysToSnakeRecursive(request))
                .then(() => {
                    this.showSaveNotification()
                    this.$emit('saved')
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save() {
            this.$refs.Form.validate()
                .then(valid => {
                    if (!valid) return

                    if (this.editing) {
                        this.update()
                    } else {
                        this.store()
                    }
                })
        },
        destroy() {
            this.showDeleteConfirmation('Deseja realmente excluir o certificado digital?', async () => {
                this.setLoading()

                this.repositories.parametrizacao.destroy()
                    .then(() => this.$emit('destroyed'))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
        download() {
            this.setLoading()

            this.repositories.parametrizacao.download()
                .then(response => {
                    const blob = new Blob([response.data], {type: "application/pdf"})
                    const link = document.createElement('a')

                    link.href = URL.createObjectURL(blob)
                    link.download = 'EVIDENCIAS.PDF'
                    link.click()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mixins: [error, formats, mask, repository, stringCase, unmask],
    mounted() {
        Promise.all([
            this.load(),
            this.create(),
        ])
    },
    watch: {
        'model.certificadoDigital'() {
            this.model.senha = null
            this.model.identificadorSolicitante = null
            this.model.cnsSolicitante = null
        },
    },
}
</script>